import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from '@shared/store';
import { useDesignVariant } from './DesignVariantProvider';
import type { WorkspaceModule } from './catalog';
import { isAssistantShortcut } from './assistantShortcut';

type Guard = (action: () => void) => void;
/** Display context only. Prompts, customer records and credentials never enter this bridge. */
export type AskPageContext = {
  page: string;
  campaign?: { id: string; title: string };
  agent?: { id: string; name: string };
  job?: { id: string; name: string };
};
const Context = createContext<{
  module: WorkspaceModule | null;
  open: (module: WorkspaceModule) => void;
  close: () => void;
  navigate: (href: string) => void;
  registerGuard: (guard: Guard | null) => void;
  changeDesign: (action: () => void) => void;
  assistantOpen: boolean;
  assistantVisited: boolean;
  closeAssistant: () => void;
  underlyingDirty: boolean;
  reportDirty: (owner: string, dirty: boolean) => void;
  pageContext: AskPageContext | null;
  reportContext: (
    owner: string,
    value: AskPageContext | null,
    surface: WorkspaceModule | null,
  ) => void;
}>({
  module: null,
  open: () => {},
  close: () => {},
  navigate: () => {},
  registerGuard: () => {},
  changeDesign: (action) => action(),
  assistantOpen: false,
  assistantVisited: false,
  closeAssistant: () => {},
  underlyingDirty: false,
  reportDirty: () => {},
  pageContext: null,
  reportContext: () => {},
});
export const useDesignWorkspace = () => useContext(Context);

/** Alternate pages share the live resource editors; Main's routed page stays mounted. */
export const DesignWorkspaceProvider = ({ children }: { children: ReactNode }) => {
  const { variant } = useDesignVariant();
  const { pathname } = useLocation();
  const user = useAppSelector((state) => state.auth.authenticatedUser);
  const scope = JSON.stringify([variant, user?.companyId, user?.id]);
  const [assistant, setAssistant] = useState<{ scope: string; open: boolean } | null>(null);
  const [contexts, setContexts] = useState<
    Map<
      string,
      {
        scope: string;
        pathname: string;
        surface: WorkspaceModule | null;
        value: AskPageContext;
      }
    >
  >(() => new Map());
  const [dirtyOwners, setDirtyOwners] = useState<Map<string, { scope: string; pathname: string }>>(
    () => new Map(),
  );
  useLayoutEffect(() => {
    setAssistant((previous) => (previous?.scope === scope ? previous : null));
    setContexts((previous) => new Map([...previous].filter(([, item]) => item.scope === scope)));
  }, [scope]);
  const reportContext = useCallback(
    (owner: string, value: AskPageContext | null, surface: WorkspaceModule | null) => {
      if (variant === 'main') return;
      setContexts((previous) => {
        const next = new Map(previous);
        if (value) next.set(owner, { scope, pathname, surface, value });
        else if (next.get(owner)?.scope === scope) next.delete(owner);
        return next;
      });
    },
    [scope, pathname, variant],
  );
  const reportDirty = useCallback(
    (owner: string, dirty: boolean) => {
      setDirtyOwners((previous) => {
        const current = previous.get(owner);
        if (dirty) {
          if (current?.scope === scope && current.pathname === pathname) return previous;
          const next = new Map(previous);
          next.set(owner, { scope, pathname });
          return next;
        }
        if (!current || current.scope !== scope || current.pathname !== pathname) return previous;
        const next = new Map(previous);
        next.delete(owner);
        return next;
      });
    },
    [scope, pathname],
  );
  const [selection, setSelection] = useState<{
    scope: string;
    pathname: string;
    module: WorkspaceModule;
  } | null>(null);
  useLayoutEffect(() => {
    setSelection((previous) => (previous?.pathname === pathname ? previous : null));
  }, [pathname]);
  const guard = useRef<Guard | null>(null);
  const routerNavigate = useNavigate();
  const module =
    selection?.scope === scope && selection.pathname === pathname && variant !== 'main'
      ? selection.module
      : null;
  useEffect(() => {
    if (variant === 'main' || (variant === 'v5' && module === 'Missions')) return;
    const shortcut = (event: KeyboardEvent) => {
      if (!isAssistantShortcut(event)) return;
      event.preventDefault();
      setAssistant((previous) => ({ scope, open: previous?.scope !== scope || !previous.open }));
    };
    window.addEventListener('keydown', shortcut);
    return () => window.removeEventListener('keydown', shortcut);
  }, [variant, module, scope]);
  const request = (action: () => void) => {
    if (guard.current) guard.current(action);
    else action();
  };
  const registerGuard = useCallback((next: Guard | null) => {
    guard.current = next;
  }, []);
  return (
    <Context.Provider
      value={{
        module,
        open: (next) => {
          if (next === 'Ask Callevate') {
            if (variant !== 'main') setAssistant({ scope, open: true });
            return;
          }
          request(() => setSelection({ scope, pathname, module: next }));
        },
        close: () => request(() => setSelection(null)),
        navigate: (href) =>
          request(() => {
            setSelection(null);
            routerNavigate(href);
          }),
        registerGuard,
        assistantOpen: variant !== 'main' && assistant?.scope === scope && assistant.open,
        assistantVisited: variant !== 'main' && assistant?.scope === scope,
        closeAssistant: () =>
          setAssistant((previous) =>
            previous?.scope === scope ? { ...previous, open: false } : previous,
          ),
        underlyingDirty: [...dirtyOwners.values()].some(
          (owner) => owner.scope === scope && owner.pathname === pathname,
        ),
        reportDirty,
        pageContext:
          variant === 'main'
            ? null
            : ([...contexts.values()]
                .reverse()
                .find(
                  (item) =>
                    item.scope === scope && item.pathname === pathname && item.surface === module,
                )?.value ?? (module ? { page: module } : null)),
        reportContext,
        changeDesign: (action) =>
          request(() => {
            setSelection(null);
            setAssistant(null);
            action();
          }),
      }}>
      {children}
    </Context.Provider>
  );
};
